// REUNIÃO GERAL (2026-09-03) — "Combinados de hoje": o bloco escrito ao vivo.
//
// É a peça que faz a reunião ter consequência. A Weekly do gestor gerou 6
// briefings automáticos e ZERO combinados — reunião sem dono e sem prazo virou
// leitura de relatório. Aqui cada linha vira uma ATIVIDADE do sistema, com
// responsável, prazo e aviso no sininho, pelo mesmo motor que a Weekly usa
// (`_converterUm`, exportado pelo handler dela).
//
// Converter é irreversível na prática (a atividade passa a existir e a pessoa é
// avisada), então o fluxo é: escrever → revisar → converter em lote, com
// confirmação. Nunca converter no mesmo clique de salvar.

import { useCallback, useEffect, useState } from 'react';
import {
  App as AntApp, Button, DatePicker, Empty, Input, Modal, Select, Space, Table, Tag, Typography,
} from 'antd';
import { CheckOutlined, CloseOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api, erroMsg, fmtData, nomeDoEmail } from './utils';
import { Combinado, ContratoOpcao, STATUS_COMBINADO } from './types';

const { Text } = Typography;

interface Props {
  reuniaoId: string;
  combinados: Combinado[];
  podeGerenciar: boolean;
  onMudou: () => void;
}

export default function CombinadosPanel({ reuniaoId, combinados, podeGerenciar, onMudou }: Props) {
  const { message, modal } = AntApp.useApp();
  const [contratos, setContratos] = useState<ContratoOpcao[]>([]);
  const [novoAberto, setNovoAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [convertendo, setConvertendo] = useState(false);
  const [selecionados, setSelecionados] = useState<string[]>([]);

  const [descricao, setDescricao] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [contratoId, setContratoId] = useState<string | undefined>();
  const [prazo, setPrazo] = useState<any>(null);

  const carregarContratos = useCallback(async () => {
    try {
      const d = await api('op_listar_contratos', {});
      const lista = Array.isArray(d?.contratos) ? d.contratos : [];
      setContratos(lista.map((c: any) => ({ id: c.id, numero: c.numero, titulo: c.titulo })));
    } catch {
      // Sem a lista de contratos o combinado ainda funciona (fica avulso) —
      // não vale bloquear a reunião por isso.
      setContratos([]);
    }
  }, []);
  useEffect(() => { if (novoAberto && !contratos.length) carregarContratos(); },
    [novoAberto, contratos.length, carregarContratos]);

  function limpar() {
    setDescricao(''); setResponsavel(''); setContratoId(undefined); setPrazo(null);
  }

  async function criar() {
    const d = descricao.trim();
    if (!d) { message.warning('Escreva o que ficou combinado.'); return; }
    setSalvando(true);
    try {
      await api('op_pauta_encam_criar', {
        reuniao_id: reuniaoId,
        descricao: d,
        responsavel_email: responsavel.trim() || undefined,
        contrato_id: contratoId || undefined,
        prazo: prazo ? prazo.format('YYYY-MM-DD') : undefined,
      });
      setNovoAberto(false); limpar(); onMudou();
    } catch (e: any) {
      message.error('Falha ao registrar: ' + erroMsg(e));
    } finally { setSalvando(false); }
  }

  async function mudarStatus(c: Combinado, status: 'aceito' | 'descartado' | 'proposto') {
    try {
      await api('op_pauta_encam_atualizar', { id: c.id, status });
      onMudou();
    } catch (e: any) {
      message.error('Falha: ' + erroMsg(e));
    }
  }

  function converter() {
    const alvos = combinados.filter((c) => selecionados.includes(c.id));
    const semDono = alvos.filter((c) => !c.responsavel_email);
    modal.confirm({
      title: `Transformar ${alvos.length} combinado(s) em tarefa?`,
      width: '94vw',
      style: { maxWidth: 620, top: 40 },
      content: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>Cada um vira uma atividade no sistema e o responsável recebe aviso.</Text>
          {semDono.length > 0 && (
            <Text type="warning">
              {semDono.length} sem responsável — a tarefa nasce sem dono. Vale preencher antes.
            </Text>
          )}
        </Space>
      ),
      okText: 'Criar as tarefas',
      cancelText: 'Voltar',
      onOk: async () => {
        setConvertendo(true);
        try {
          const d = await api('op_pauta_encam_converter',
            { reuniao_id: reuniaoId, ids: selecionados }, 60000);
          const feitos = Array.isArray(d?.convertidos) ? d.convertidos.length : 0;
          const erros = Array.isArray(d?.erros) ? d.erros : [];
          if (feitos) message.success(`${feitos} tarefa(s) criada(s).`);
          if (erros.length) {
            message.warning(`${erros.length} não deu: ${erros.map((x: any) => x.erro).join(' · ')}`);
          }
          setSelecionados([]); onMudou();
        } catch (e: any) {
          message.error('Falha ao converter: ' + erroMsg(e));
        } finally { setConvertendo(false); }
      },
    });
  }

  const colunas = [
    {
      title: 'O que ficou combinado', dataIndex: 'descricao', key: 'descricao',
      render: (v: string, r: Combinado) => (
        <Space direction="vertical" size={0}>
          <Text>{v}</Text>
          {r.contrato_numero && <Text type="secondary" style={{ fontSize: 12 }}>{r.contrato_numero}</Text>}
        </Space>
      ),
    },
    {
      title: 'Dono', dataIndex: 'responsavel_email', key: 'dono', width: 150,
      render: (v: string | null) => v
        ? <Text>{nomeDoEmail(v)}</Text>
        : <Text type="warning">sem dono</Text>,
    },
    {
      title: 'Prazo', dataIndex: 'prazo', key: 'prazo', width: 110,
      responsive: ['sm' as const],
      render: (v: string | null) => v ? fmtData(v) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Situação', dataIndex: 'status', key: 'status', width: 130,
      render: (v: Combinado['status']) => {
        const s = STATUS_COMBINADO[v] || { label: v, cor: 'default' };
        return <Tag color={s.cor}>{s.label}</Tag>;
      },
    },
    ...(podeGerenciar ? [{
      title: '', key: 'acoes', width: 100,
      render: (_: any, r: Combinado) => {
        if (r.status === 'convertido') return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        return (
          <Space size={4}>
            {r.status !== 'aceito' && (
              <Button size="small" type="text" title="Aceitar"
                icon={<CheckOutlined />} onClick={() => mudarStatus(r, 'aceito')} />
            )}
            {r.status !== 'descartado' && (
              <Button size="small" type="text" title="Descartar"
                icon={<CloseOutlined />} onClick={() => mudarStatus(r, 'descartado')} />
            )}
            {r.status === 'descartado' && (
              <Button size="small" type="text" onClick={() => mudarStatus(r, 'proposto')}>reativar</Button>
            )}
          </Space>
        );
      },
    }] : []),
  ];

  // Só o que ainda não virou tarefa pode ser selecionado.
  const conversiveis = combinados.filter((c) => c.status === 'proposto' || c.status === 'aceito');

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {podeGerenciar && (
        <Space wrap>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setNovoAberto(true)}>
            Registrar combinado
          </Button>
          <Button icon={<ThunderboltOutlined />} loading={convertendo}
            disabled={!selecionados.length} onClick={converter}>
            Virar tarefa{selecionados.length ? ` (${selecionados.length})` : ''}
          </Button>
        </Space>
      )}

      {combinados.length === 0
        ? <Empty description={podeGerenciar
            ? 'Nada registrado ainda. Cada decisão da reunião entra aqui, com dono e prazo.'
            : 'Nada foi registrado nesta reunião.'} />
        : (
          <Table<Combinado>
            rowKey="id" size="small" columns={colunas} dataSource={combinados}
            pagination={false} tableLayout="fixed"
            rowSelection={podeGerenciar ? {
              selectedRowKeys: selecionados,
              onChange: (ks) => setSelecionados(ks as string[]),
              getCheckboxProps: (r) => ({
                disabled: !conversiveis.some((c) => c.id === r.id),
              }),
            } : undefined}
          />
        )}

      <Modal
        title="O que ficou combinado"
        open={novoAberto}
        onCancel={() => { setNovoAberto(false); limpar(); }}
        onOk={criar}
        confirmLoading={salvando}
        okText="Registrar"
        cancelText="Cancelar"
        width="94vw"
        style={{ maxWidth: 720, top: 24 }}
        styles={{ body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' } }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong>Combinado</Text>
            <Input.TextArea rows={3} value={descricao} maxLength={1000} showCount
              placeholder="Ex.: levantar a curva-chave da estação do Gravataí e trazer na próxima"
              onChange={(e) => setDescricao(e.target.value)} />
          </div>
          <Space wrap style={{ width: '100%' }} size="middle">
            <div style={{ minWidth: 260 }}>
              <Text strong>Quem faz</Text><br />
              <Input value={responsavel} placeholder="email@hydrodataeng.com.br"
                onChange={(e) => setResponsavel(e.target.value)} style={{ width: 260 }} />
            </div>
            <div>
              <Text strong>Até quando</Text><br />
              <DatePicker value={prazo} onChange={setPrazo} format="DD/MM/YYYY"
                placeholder="prazo" style={{ width: 170 }}
                disabledDate={(d) => d && d.isBefore(dayjs().startOf('day'))} />
            </div>
            <div style={{ minWidth: 280 }}>
              <Text strong>Contrato (se for de um projeto)</Text><br />
              <Select allowClear showSearch style={{ width: 280 }} value={contratoId}
                onChange={setContratoId} placeholder="nenhum — tarefa avulsa"
                optionFilterProp="label"
                options={contratos.map((c) => ({
                  value: c.id,
                  label: `${c.numero || 's/ nº'}${c.titulo ? ' — ' + c.titulo : ''}`,
                }))} />
            </div>
          </Space>
          <Text type="secondary">
            Ao virar tarefa, ela aparece em Atividades para o responsável e o sininho dele acende.
          </Text>
        </Space>
      </Modal>
    </Space>
  );
}
